import { type Property } from "@/types/property";
import { useDashboardAggregates } from "./useDashboardAggregates";
import { useMemo } from "react";

interface UseDashboardMetricsProps {
  selectedYear: string;
  selectedMonth: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  properties: Property[];
}

const useDashboardMetrics = ({
  selectedYear,
  selectedMonth,
  selectedProperties,
  selectedOrganizations,
  properties,
}: UseDashboardMetricsProps) => {
  const year = parseInt(selectedYear);
  const month = parseInt(selectedMonth);

  // Fetch aggregated metrics from database function (fast, no pagination limits)
  const { data: aggregates, isLoading: isLoadingAggregates } = useDashboardAggregates({
    year,
    month,
    propertyIds: selectedProperties.length > 0 ? selectedProperties : undefined,
    organizationIds: selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
  });

  const metrics = useMemo(() => {
    if (!aggregates) {
      return null;
    }

    // Main metrics come directly from aggregates (accurate, no pagination issues)
    const receivedIncome = aggregates.received_income;
    const futureIncome = aggregates.future_income;
    const totalExpenses = aggregates.total_expenses;

    /**
     * ============================================================================
     * CÁLCULO DE TASA DE OCUPACIÓN
     * ============================================================================
     * 
     * Fórmula: (Días Reservados / Días Disponibles) × 100
     * 
     * Componentes:
     * ------------
     * 1. Días Reservados (total_reserved_days):
     *    - Suma de noches ocupadas por reservaciones en el mes
     *    - Se calcula como check_out - check_in para cada reservación
     *    - Si la reservación cruza límites del mes, se ajusta a los límites
     *    - IMPORTANTE: El día de check-out NO se cuenta como ocupado 
     *      (la noche termina ese día, la propiedad queda disponible)
     *    - NO incluye reservaciones canceladas
     * 
     * 2. Días Disponibles = Total Días Operativos - Días Bloqueados
     *    a) Total Días Operativos (total_possible_days):
     *       - Para cada propiedad: cuenta días desde MAX(operation_start_date, primer_día_mes)
     *       - Hasta el último día del mes
     *       - Si la propiedad inició operación después del fin del mes, contribuye 0 días
     *       - Esto corrige el cálculo para propiedades con inicio de operación durante el mes
     * 
     *    b) Días Bloqueados (total_blocked_days):
     *       - Días marcados manualmente como no disponibles
     *       - Incluye: mantenimiento, uso personal, bloqueos de iCal, etc.
     *       - Se RESTAN del denominador porque no estaban disponibles para rentar
     * 
     * Ejemplo Práctico: EC.09_PDS-12C en Noviembre 2025
     * --------------------------------------------------
     * - operation_start_date: 2025-11-11
     * - Días operativos: 30 - 11 + 1 = 20 días (del 11 al 30 de noviembre)
     * - Días bloqueados: 1
     * - Días disponibles: 20 - 1 = 19 días
     * - Días reservados: 15 días
     * - Tasa de ocupación: (15 / 19) × 100 = 78.95%
     * 
     * Ejemplo cuando la propiedad NO operaba: EC.09_PDS-12C en Octubre 2025
     * ----------------------------------------------------------------------
     * - operation_start_date: 2025-11-11 (después del mes consultado)
     * - Días operativos: 0 días
     * - Ocupación: 0% (la propiedad no contribuye al cálculo)
     * 
     * Ejemplo con múltiples propiedades en Noviembre 2025:
     * -----------------------------------------------------
     * - EC.09_PDS-12C: operation_start_date = 2025-11-11 → 20 días (11-30)
     * - EC.08_PLH-158: operation_start_date = 2025-11-01 → 30 días (1-30)
     * - Total días operativos: 20 + 30 = 50 días
     * - Días bloqueados (ambas propiedades): 2
     * - Días disponibles: 50 - 2 = 48 días
     * 
     * Consideraciones Importantes:
     * ----------------------------
     * - Si availableDays = 0 (todos los días bloqueados o propiedad no operaba), la tasa es 0%
     * - Las reservaciones que cruzan meses solo cuentan los días dentro del mes
     * - Los días bloqueados se excluyen del cálculo para reflejar días 
     *   realmente disponibles para rentar
     * - El resultado se redondea a 2 decimales para visualización
     * ============================================================================
     */
    const totalBlockedDays = aggregates.total_blocked_days;
    const totalPossibleDays = aggregates.total_possible_days;
    const availableDays = Math.max(0, totalPossibleDays - totalBlockedDays);

    const occupancyRate =
      availableDays > 0 ? (aggregates.total_reserved_days / availableDays) * 100 : 0;

    /**
     * ============================================================================
     * CÁLCULO DE COSTO POR NOCHE PROMEDIO
     * ============================================================================
     * 
     * Fórmula: Σ MAX(estimated_prorrateado, ingresos_prorrateados) / Σ noches_en_mes
     * 
     * Componentes:
     * ------------
     * 1. Numerador (avg_cost_per_night_numerator):
     *    - Para cada reservación con check_in en el mes seleccionado:
     *      a) Calcula estimated_payout prorrateado = estimated_payout × (noches_en_mes / noches_totales)
     *      b) Calcula ingresos_reales prorrateados = SUM(property_income.payout) × (noches_en_mes / noches_totales)
     *      c) Usa MAX(a, b) para capturar el valor más alto (ingresos adicionales o estimado completo)
     *    - Suma todos los MAX() de las reservaciones del mes
     *    - NO incluye reservaciones canceladas
     *    - Independiente del estatus de pago
     * 
     * 2. Denominador (avg_cost_per_night_denominator):
     *    - Suma de noches que caen DENTRO del mes para cada reservación
     *    - Si una reservación cruza meses, solo cuenta las noches del mes seleccionado
     *    - Ejemplo: check_in 30 dic, check_out 2 ene = 1 noche en diciembre
     * 
     * Ejemplo Práctico: Reservación que cruza meses
     * -----------------------------------------------
     * - check_in: 30 dic 2025, check_out: 2 ene 2026
     * - nights: 3 (totales), estimated_payout: $10,520.62
     * - Ingresos registrados: $0 (pending)
     * 
     * Para el cálculo de Diciembre 2025:
     * - Noches en diciembre: 1 (solo 30 dic)
     * - Factor prorrateo: 1/3 = 0.333
     * - Estimated prorrateado: $10,520.62 × 0.333 = $3,506.87
     * - Ingresos prorrateados: $0 × 0.333 = $0
     * - MAX(): $3,506.87
     * - Contribución: $3,506.87 / 1 noche
     * 
     * Ventajas de esta lógica:
     * -------------------------
     * - ✅ Numerador y denominador alineados temporalmente (ambos basados en check_in)
     * - ✅ Independiente del estatus de pago (incluye pending, partial, paid)
     * - ✅ Prorrateo correcto para reservaciones que cruzan meses
     * - ✅ MAX() captura ingresos adicionales (late checkout, extras) sin subestimar
     * - ✅ Refleja el precio promedio real por noche de todas las reservaciones
     * ============================================================================
     */
    const averageCostPerNight =
      aggregates.avg_cost_per_night_denominator > 0
        ? aggregates.avg_cost_per_night_numerator / aggregates.avg_cost_per_night_denominator
        : 0;

    /**
     * ============================================================================
     * CÁLCULO DE NOCHES POR RENTAR
     * ============================================================================
     * 
     * Fórmula: Contar días desde fecha_actual hasta fin_de_mes que estén libres
     * 
     * Componentes:
     * ------------
     * 1. Rango de fechas:
     *    - Inicio: MAX(fecha_actual, primer_día_del_mes)
     *    - Fin: último_día_del_mes (inclusive)
     * 
     * 2. Día disponible = NO bloqueado AND NO reservado
     *    - NO bloqueado: No existe en property_blocked_days
     *    - NO reservado: No hay booking donde check_in <= día < check_out
     * 
     * 3. Día de check-out:
     *    - Se considera DISPONIBLE (el huésped se va, la propiedad queda libre)
     * 
     * 4. Última noche del mes:
     *    - La noche del 30 de noviembre (check-in el 30) se cuenta como noche de noviembre
     * 
     * Ejemplo: PDS-7E en Noviembre 2025 (fecha actual: 26 nov)
     * ---------------------------------------------------------
     * - Días restantes del mes: 26, 27, 28, 29, 30
     * - Días reservados: 26 (reservación 27-29), 27, 28
     * - Días disponibles: 29 y 30
     * - Noches por rentar: 2
     * 
     * Consideraciones Importantes:
     * ----------------------------
     * - NO se cuentan noches pasadas (incluso del mismo mes)
     * - La fecha actual SÍ se cuenta si está libre
     * - Si hay múltiples propiedades, se suman todas las noches disponibles
     * - Solo considera propiedades seleccionadas en el filtro
     * ============================================================================
     */
    const nightsToRent = aggregates.nights_to_rent;

    /**
     * ============================================================================
     * CÁLCULO DE TASA DE OCUPACIÓN 7 DÍAS
     * ============================================================================
     * Igual que la tasa de ocupación mensual pero solo para los próximos 7 días
     */
    const occupancyRate7Days = aggregates.occupancy_rate_7_days;
    const availableDays7 = Math.max(
      0,
      occupancyRate7Days.possible_days - occupancyRate7Days.blocked_days
    );
    const rate7Days =
      availableDays7 > 0
        ? Math.round((occupancyRate7Days.reserved_days / availableDays7) * 100)
        : 0;

    /**
     * ============================================================================
     * CÁLCULO DE TASA DE OCUPACIÓN 15 DÍAS
     * ============================================================================
     * Igual que la tasa de ocupación mensual pero solo para los próximos 15 días
     */
    const occupancyRate15Days = aggregates.occupancy_rate_15_days;
    const availableDays15 = Math.max(
      0,
      occupancyRate15Days.possible_days - occupancyRate15Days.blocked_days
    );
    const rate15Days =
      availableDays15 > 0
        ? Math.round((occupancyRate15Days.reserved_days / availableDays15) * 100)
        : 0;

    // Calculate accumulated profit (simplified - returns zeros for now)
    // TODO: Create separate backend function for full year accumulated data
    const accumulatedProfit = Array.from({ length: 12 }, () => 0);

    return {
      futureIncome,
      receivedIncome,
      totalExpenses,
      occupancyRate: Math.round(occupancyRate * 100) / 100,
      averageCostPerNight: Math.round(averageCostPerNight * 100) / 100,
      nightsToRent,
      occupancyRate7Days: rate7Days,
      occupancyRate7DaysDateStart: occupancyRate7Days.date_start,
      occupancyRate7DaysDateEnd: occupancyRate7Days.date_end,
      occupancyRate15Days: rate15Days,
      occupancyRate15DaysDateStart: occupancyRate15Days.date_start,
      occupancyRate15DaysDateEnd: occupancyRate15Days.date_end,
      accumulatedProfit,
    };
  }, [aggregates]);

  return {
    metrics,
    isLoading: isLoadingAggregates,
  };
};

export default useDashboardMetrics;
